// @flow
import api from '../api/searchSuggestions';
import cloneDeep from 'lodash/cloneDeep';
import { createSelector } from 'reselect';
import { getDeployment } from './config';
import type { GlobalState } from '../rootReducer';

/* Action Types */
export const SEARCH_SUGGESTIONS_FAIL = 'la/ui/searchSuggestions/FAIL';
export const SEARCH_SUGGESTIONS_REQUEST = 'la/ui/searchSuggestions/REQUEST';
export const SEARCH_SUGGESTIONS_SUCCESS = 'la/ui/searchSuggestions/SUCCESS';

// reducer
const DEFAULT_STATE = {
    error: false,
    secondarySuggestions: [],
    submitted: false,
    success: false,
    suggestions: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case SEARCH_SUGGESTIONS_REQUEST:
            return {
                ...state,
                submitted: true,
                success: false,
            };
        case SEARCH_SUGGESTIONS_SUCCESS:
            if (action.meta.secondary) {
                return {
                    ...state,
                    secondarySuggestions: cloneDeep(action.payload),
                    submitted: false,
                    success: true,
                };
            }
            return {
                ...state,
                submitted: false,
                success: true,
                suggestions: cloneDeep(action.payload),
            };
        case SEARCH_SUGGESTIONS_FAIL:
            return {
                ...state,
                error: true,
                submitted: false,
                success: false,
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.searchSuggestions;

const getUiSearchSuggestions = createSelector(stateSelector, state => state);

export const getSecondarySearchSuggestions = createSelector(getUiSearchSuggestions, uiSearchSuggestions => {
    if (uiSearchSuggestions.secondarySuggestions) {
        return uiSearchSuggestions.secondarySuggestions.reduce(
            (refinedResult, result) => {
                if (result.label === 'Auction Houses') return refinedResult;
                if (result.houseId) {
                    refinedResult.houses.push(result);
                } else {
                    refinedResult.suggestions.push(result);
                }
                return refinedResult;
            },
            { houses: [], suggestions: [] }
        );
    }
    return { houses: [], suggestions: [] };
});

export const getSearchSuggestions = createSelector(getUiSearchSuggestions, uiSearchSuggestions => {
    if (uiSearchSuggestions.suggestions) {
        let reduced = uiSearchSuggestions.suggestions.reduce(
            (refinedResult, result) => {
                if (result.label === 'Auction Houses') return refinedResult;
                if (result.houseId) {
                    refinedResult.auctionHouses.push(result);
                } else {
                    refinedResult.auctionItems.push(result);
                }
                return refinedResult;
            },
            { auctionHouses: [], auctionItems: [] }
        );
        let suggestions = [
            {
                suggestions: reduced.auctionItems,
                title: '',
            },
        ];
        if (reduced.auctionHouses.length > 0) {
            suggestions.push({
                suggestions: reduced.auctionHouses,
                title: 'Auction Houses',
            });
        }
        return suggestions;
    }
});

export const getSearchSuggestionsLoading = createSelector(getUiSearchSuggestions, uiSearchSuggestions =>
    Boolean(uiSearchSuggestions.submitted)
);

/* ACTION CREATORS */
export const fetchSearchSuggestions = (term: string, secondary: boolean = false) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);

        dispatch({
            payload: term,
            type: SEARCH_SUGGESTIONS_REQUEST,
        });
        // $FlowFixMe
        const response = await api.fetchSuggestions({ deployment, term });
        dispatch({
            meta: { secondary },
            payload: response.payload,
            type: SEARCH_SUGGESTIONS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { term },
            payload: error,
            type: SEARCH_SUGGESTIONS_FAIL,
        });
    }
};
